const PRODUCT_UPDATE_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const PRODUCT_UPDATE_TIME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});

export const PRODUCT_UPDATE_CATEGORY_META = {
  feature: {
    label: "Fonctionnalité",
    tone: "info",
  },
  improvement: {
    label: "Amélioration",
    tone: "success",
  },
  fix: {
    label: "Correctif",
    tone: "warning",
  },
  internal: {
    label: "Interne",
    tone: "outline",
  },
};

const PRODUCT_UPDATE_ANNOUNCE_TONES = new Set(["default", "danger"]);

// Modifiez d'abord cette liste.
// body accepte un template multiline pour garder les retours de chariot visibles dans le code.
// Les lignes commencant par "-", "*" ou "•" deviennent des puces dans le journal.
// Plusieurs entrées avec announce: true peuvent s'afficher en même temps dans les bandeaux.
// Ajoutez announceTone: "danger" si vous voulez un bandeau rouge, par exemple pour une maintenance.
// Si vous ne renseignez pas id, l'identifiant est recalculé depuis la date, le titre et le body.
// Donc un nouveau point ajouté dans body peut refaire apparaître le bandeau, même sans changer la date ni le titre.

//{
//    date: "2026-04-19 - 14h:01",
//    title: "Maintenance en cours",
//    body: `
//Une maintenance est actuellement en cours sur le SAAS.
//
//- Certaines actions peuvent être ralenties pendant l'intervention.
//- Évitez de relancer plusieurs fois la même opération.
//- Un nouveau bandeau confirmera la fin de maintenance.
//`,
//    announce: true,
//    announceTone: "danger",
//  },

const PRODUCT_UPDATES = [
  // Les nouveautés passent maintenant par la Gestion du Journal ; cette liste ne sert qu'aux bandeaux de maintenance.
].map((update, index) => normalizeProductUpdate(update, index)).sort((leftUpdate, rightUpdate) => (
  getProductUpdateTimestamp(rightUpdate.date) - getProductUpdateTimestamp(leftUpdate.date)
));

function parseProductUpdateDate(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const normalizedValue = value.trim();
  const localDateTimeMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s*-\s*(\d{1,2})(?:h:?|:)(\d{2}))?$/i);

  if (localDateTimeMatch) {
    const [, year, month, day, rawHour = "0", rawMinute = "0"] = localDateTimeMatch;
    const parsedYear = Number(year);
    const parsedMonth = Number(month);
    const parsedDay = Number(day);
    const parsedHour = Number(rawHour);
    const parsedMinute = Number(rawMinute);

    if (parsedHour < 0 || parsedHour > 23 || parsedMinute < 0 || parsedMinute > 59) {
      return null;
    }

    const parsedDate = new Date(parsedYear, parsedMonth - 1, parsedDay, parsedHour, parsedMinute, 0, 0);

    if (
      Number.isNaN(parsedDate.getTime())
      || parsedDate.getFullYear() !== parsedYear
      || parsedDate.getMonth() !== parsedMonth - 1
      || parsedDate.getDate() !== parsedDay
      || parsedDate.getHours() !== parsedHour
      || parsedDate.getMinutes() !== parsedMinute
    ) {
      return null;
    }

    return {
      date: parsedDate,
      hasTime: Boolean(localDateTimeMatch[4] && localDateTimeMatch[5]),
    };
  }

  const timestamp = Date.parse(normalizedValue);
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return {
    date: new Date(timestamp),
    hasTime: /(?:\d{1,2}:\d{2}|\d{1,2}h:?\d{2})/i.test(normalizedValue),
  };
}

export function getProductUpdateTimestamp(value) {
  const parsedDate = parseProductUpdateDate(value);
  return parsedDate ? parsedDate.date.getTime() : 0;
}

export function formatProductUpdateDate(value) {
  const parsedDate = parseProductUpdateDate(value);

  if (!parsedDate) {
    return typeof value === "string" ? value : "Date inconnue";
  }

  const formattedDate = PRODUCT_UPDATE_DATE_FORMATTER.format(parsedDate.date);

  if (!parsedDate.hasTime) {
    return formattedDate;
  }

  return `${formattedDate} • ${PRODUCT_UPDATE_TIME_FORMATTER.format(parsedDate.date)}`;
}

export function getProductUpdateBodyBlocks(value) {
  const normalizedValue = normalizeProductUpdateText(value);

  if (!normalizedValue) {
    return [];
  }

  const blocks = [];
  const lines = normalizedValue.split("\n");
  let paragraphLines = [];
  let listItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return;
    }

    blocks.push({
      text: paragraphLines.join("\n"),
      type: "paragraph",
    });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length) {
      return;
    }

    blocks.push({
      items: listItems,
      type: "list",
    });
    listItems = [];
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushParagraph();
      flushList();
      return;
    }

    const bulletMatch = trimmedLine.match(/^[-*•]\s+(.*)$/);

    if (bulletMatch) {
      flushParagraph();
      listItems.push(bulletMatch[1].trim());
      return;
    }

    flushList();
    paragraphLines.push(trimmedLine);
  });

  flushParagraph();
  flushList();

  return blocks;
}

export function getProductUpdatePreviewText(value) {
  const bodyBlocks = getProductUpdateBodyBlocks(value);

  if (!bodyBlocks.length) {
    return "";
  }

  const firstParagraphBlock = bodyBlocks.find((block) => block.type === "paragraph" && typeof block.text === "string" && block.text.trim());

  if (firstParagraphBlock) {
    return firstParagraphBlock.text;
  }

  const firstListBlock = bodyBlocks.find((block) => block.type === "list" && Array.isArray(block.items) && block.items.length > 0);

  return firstListBlock?.items?.[0] || "";
}

function normalizeProductUpdateText(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();
}

function slugifyProductUpdateValue(value) {
  return normalizeProductUpdateText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function hashProductUpdateValue(value) {
  const normalizedValue = normalizeProductUpdateText(value);

  if (!normalizedValue) {
    return "empty";
  }

  let hash = 0;

  for (let index = 0; index < normalizedValue.length; index += 1) {
    hash = ((hash * 31) + normalizedValue.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36);
}

function normalizeProductUpdateAnnounceTone(value) {
  if (typeof value !== "string") {
    return "default";
  }

  const normalizedValue = value.trim().toLowerCase();
  return PRODUCT_UPDATE_ANNOUNCE_TONES.has(normalizedValue) ? normalizedValue : "default";
}

function buildProductUpdateId(update, index) {
  const explicitId = normalizeProductUpdateText(update?.id);

  if (explicitId) {
    return explicitId;
  }

  const datePart = normalizeProductUpdateText(update?.date) || `update-${index + 1}`;
  const titlePart = slugifyProductUpdateValue(update?.title) || `entree-${index + 1}`;
  const bodyPart = hashProductUpdateValue(update?.body);
  const announceTone = normalizeProductUpdateAnnounceTone(update?.announceTone);

  if (announceTone === "default") {
    return `${datePart}-${titlePart}-${bodyPart}`;
  }

  return `${datePart}-${titlePart}-${announceTone}-${bodyPart}`;
}

function normalizeProductUpdate(update, index) {
  const normalizedDate = normalizeProductUpdateText(update?.date);
  const normalizedTitle = normalizeProductUpdateText(update?.title) || `Mise à jour ${index + 1}`;
  const normalizedBody = normalizeProductUpdateText(update?.body);
  const normalizedAnnounceTone = normalizeProductUpdateAnnounceTone(update?.announceTone);
  const normalizedCategory = typeof update?.category === "string" && PRODUCT_UPDATE_CATEGORY_META[update.category]
    ? update.category
    : "improvement";

  return {
    announce: update?.announce === true,
    announceTone: normalizedAnnounceTone,
    body: normalizedBody,
    category: normalizedCategory,
    date: normalizedDate,
    id: buildProductUpdateId({ ...update, date: normalizedDate, title: normalizedTitle }, index),
    title: normalizedTitle,
  };
}

export default PRODUCT_UPDATES;